import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import find from 'lodash-es/find';
import uniq from 'lodash-es/uniq';
import includes from 'lodash-es/includes';
import each from 'lodash-es/each';
import filter from 'lodash-es/filter';

import CONSTANTS from '@constants';
import { IFBLanguage, IFBLanguageWithName } from '@interfaces';
import browserHelper from '@utils/browser-helper';

import { CompanyService } from './company.service';
import { CoreDataResource } from '../modules/resources/core-data.resource';
import { TranslateService } from '../modules/translate/services/translate.service';
import { featureFlags } from '@utils/feature-flags';

@Injectable({
  providedIn: 'root'
})
export class LanguagesService {
  banned: string[] = [];
  constants = CONSTANTS;

  constructor(
    private coreDataResource: CoreDataResource,
    private companyService: CompanyService,
    private translateService: TranslateService
  ) {}

  // Use it to get list of languages on the platform,
  // returns only allowed languages
  public getAllowed() {
    const hasSubdomain = this.companyService.subdomain;
    const hasCompanyLanguages =
      this.companyService.data &&
      this.companyService.data.languages &&
      this.companyService.data.languages.enabledLocales &&
      this.companyService.data.languages.enabledLocales.length;
    if (hasSubdomain && hasCompanyLanguages) {
      // Make it match CoreLanguages structure
      const locales = uniq(this.companyService.data.languages.enabledLocales);
      let languages: IFBLanguage[] = locales.map((locale: string) => {
        const overridenLocale = this.translateService.overrideLocale(locale);
        return { locale: overridenLocale };
      });
      languages = this.filterSupportedLanguages(languages);
      languages = this.filterOutBanned(languages);

      return of(this.addTranslateKey(languages));
    } else {
      return this.coreDataResource.getLanguages().pipe(
        map(languages => {
          const allowedLanguages = this.filterOutBanned(languages);
          return this.addTranslateKey(allowedLanguages);
        })
      );
    }
  }

  addTranslateKey(languages: IFBLanguage[]): IFBLanguageWithName[] {
    return languages.map((language: IFBLanguage) => {
      const name = find(this.constants.LANGUAGES, (val: string, key: string) => key === language.locale);
      const { locale } = language;

      return { name, locale } as IFBLanguageWithName;
    }) as IFBLanguageWithName[];
  }

  checkFeatureToggled() {
    each(this.constants.FEATURE_FLAGGED_LANGUAGES, (locale, key) => {
      if (!featureFlags.isOn(key)) {
        this.markAsBanned(locale);
      }
    });

    const isCurrentLocaleBanned = this.banned.indexOf(this.translateService.getRawLocale()) !== -1;
    if (this.banned.length && isCurrentLocaleBanned) {
      this.translateService.setLocale(browserHelper.forceLocaleRecalculation(this.banned));
    }
  }

  filterOutBanned(languages: IFBLanguage[]) {
    return languages.filter(language => !includes(this.banned, language.locale));
  }

  filterSupportedLanguages(languages: IFBLanguage[]) {
    return filter(languages, lang => {
      return this.constants.LANGUAGES.hasOwnProperty(lang.locale);
    });
  }

  markAsBanned(locale: string) {
    this.banned.push(locale);

    // clean assets
    delete this.constants.DEFAULT_REGISTRATION_TEXTS[locale];
    delete this.constants.DEFAULT_SHARING_TEXTS.email[locale];
    delete this.constants.LANGUAGES[locale];
  }
}
